'use client';

import { Sample, Group } from '@/sharedTypes/Api';
import React, { useEffect, useState, useCallback } from 'react';
import { apiSwaggerClientSide } from '@/shared/api';
import { useLanguageFromPath } from '@/hooks/useLanguageFromPath';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { CatalogueFilters } from '../views/CatalogueTableView';
import { ButtonShadcn } from '@/components/ui/button-shadcn';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { AdminCatalogueView, SelectedSample } from '../Catalogue';

const PAGE_SIZE = 20;

interface CatalogueTableProps {
  filters: CatalogueFilters;
  page: number;
  onPageChange: (page: number) => void;
  selectedSamples: SelectedSample[];
  onSelectionChange: (samples: SelectedSample[]) => void;
  setView: React.Dispatch<React.SetStateAction<AdminCatalogueView>>;
}

interface PaginationData {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}
// ** NEED TO ADD CACHE IN ORDER TO NOT ALWAYS MAKE FRESH REQUESTS
const CatalogueTable = ({
  filters,
  page,
  onPageChange,
  selectedSamples,
  onSelectionChange,
  setView
}: CatalogueTableProps) => {
  const language = useLanguageFromPath();
  const otherLanguage = language === 'en' ? 'ru' : 'en'
  
  const [samples, setSamples] = useState<Sample[]>([]);
  const [pagination, setPagination] = useState<PaginationData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSamples = useCallback(async () => {
    setLoading(true);
    try {
      const query: Record<string, string | number> = {
        page,
        pageSize: PAGE_SIZE,
      };

      if (filters.search) {
        query.search = filters.search;
      }
      if (filters.visibility) {
        query.visibility = filters.visibility;
      }
      if (filters.group) {
        query.group = filters.group;
      }

      const response = await apiSwaggerClientSide.admin.samplesList(
        query as Parameters<typeof apiSwaggerClientSide.admin.samplesList>[0]
      );

      if (response.data?.success && response.data.data) {
        setSamples(response.data.data.samples || []);
        setPagination(response.data.data.pagination as PaginationData || null);
      }
    } catch (error) {
      console.error('Failed to fetch samples:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchSamples();
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchSamples]);

  const mapSampleToSelected = (sample: Sample): SelectedSample => ({
    id: sample._id,
    sample: {
      title: sample.metadata?.title?.[language] || sample.metadata?.title?.[otherLanguage] || sample.identifier,
      accessControl: {
        allowedGroups: (sample.accessControl?.allowedGroups || []).map((g) =>
          typeof g === 'string' ? { _id: g, name: g } : { _id: (g as Group)._id || '', name: (g as Group).name || '' }
        ),
        allowedUsers: (sample.accessControl?.allowedUsers || []).map((u) =>
          typeof u === 'string' ? { sub: u, username: u } : { sub: u.sub || '', username: u.username || '' }
        ),
      },
      isPrivate: sample.isPrivate || false
    },
  });

  const selectedIds = selectedSamples.map((s) => s.id);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      onSelectionChange(samples.map(mapSampleToSelected));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectOne = (sample: Sample, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedSamples, mapSampleToSelected(sample)]);
    } else {
      onSelectionChange(selectedSamples.filter((s) => s.id !== sample._id));
    }
  };

  const allSelected = samples.length > 0 && selectedSamples.length === samples.length;
  const someSelected = selectedSamples.length > 0 && selectedSamples.length < samples.length;

  const getAccessDisplay = (sample: Sample) => {
    const isHidden = sample.isPrivate

    if(isHidden) {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded bg-gray-600 text-gray-800">
          Hidden
        </span>
      );
    }

  const isRestricted = (sample.accessControl?.allowedGroups?.length ?? 0) > 0 || (sample.accessControl?.allowedUsers?.length ?? 0) > 0
  
    if (isRestricted) {
      const allowedGroups = sample.accessControl?.allowedGroups || [];
      const allowedUsers = sample.accessControl?.allowedUsers || [];

      const tooltipContent = (
        <div className="text-xs">
          {allowedGroups.length > 0 && (
            <div>
              <span className="font-semibold">Groups:</span>{' '}
              {allowedGroups
                .map((g) => (typeof g === 'string' ? g : (g as Group).name))
                .join(', ')}
            </div>
          )}
          {allowedUsers.length > 0 && (
            <div>
              <span className="font-semibold">Users:</span>{' '}
              {allowedUsers
                .map((u) => (typeof u === 'string' ? u : u.username))
                .join(', ')}
            </div>
          )}
          {allowedGroups.length === 0 && allowedUsers.length === 0 && (
            <div>No specific access configured</div>
          )}
        </div>
      );

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-800 cursor-help">
              Restricted
            </span>
          </TooltipTrigger>
          <TooltipContent side="left">{tooltipContent}</TooltipContent>
        </Tooltip>
      );
    }

    return (
      <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800">
        Public
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <div className="border rounded-lg overflow-hidden flex-1 min-h-0 overflow-y-auto">
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
              <th className="w-12 px-4 py-3"><Skeleton className="h-4 w-4" /></th>
              <th className="px-4 py-3 text-left"><Skeleton className="h-4 w-20" /></th>
              <th className="px-4 py-3 text-left"><Skeleton className="h-4 w-16" /></th>
              <th className="px-4 py-3 text-left"><Skeleton className="h-4 w-16" /></th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 10 }).map((_, i) => (
              <tr key={i} className="border-t">
                <td className="px-4 py-3"><Skeleton className="h-4 w-4" /></td>
                <td className="px-4 py-3"><Skeleton className="h-4 w-48" /></td>
                <td className="px-4 py-3"><Skeleton className="h-4 w-32" /></td>
                <td className="px-4 py-3"><Skeleton className="h-6 w-16" /></td>
              </tr>
            ))}
          </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="border rounded-lg overflow-hidden flex-1 min-h-0 overflow-y-auto">
        <table className="w-full">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="w-12 px-4 py-3">
                <Checkbox
                  checked={allSelected}
                  ref={(el) => {
                    if (el) (el as HTMLButtonElement).dataset.state = someSelected ? 'indeterminate' : allSelected ? 'checked' : 'unchecked';
                  }}
                  onCheckedChange={handleSelectAll}
                />
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                Title
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                Author
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                Access
              </th>
            </tr>
          </thead>
          <tbody>
            {samples.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                  No samples found
                </td>
              </tr>
            ) : (
              samples.map((sample) => {
                return (
                  <tr
                    key={sample._id}
                    className="border-t hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <Checkbox
                        checked={selectedIds.includes(sample._id)}
                        onCheckedChange={(checked) =>
                          handleSelectOne(sample, checked as boolean)
                        }
                      />
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {sample.metadata?.title?.[language] ||
                        sample.metadata?.title?.[otherLanguage] ||
                        sample.identifier}
                    </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {sample.metadata?.author?.[language] ||
                      sample.metadata?.author?.[otherLanguage] ||
                      '-'}
                  </td>
                  <td className="px-4 py-3">{getAccessDisplay(sample)}</td>
                </tr>
              );
            })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between my-4 px-2">
          <div className="text-sm text-gray-600">
            Showing {(page - 1) * PAGE_SIZE + 1} -{' '}
            {Math.min(page * PAGE_SIZE, pagination.total)} of {pagination.total}
          </div>
          <div className="flex items-center gap-2">
            <ButtonShadcn
              variant="outline"
              size="sm"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </ButtonShadcn>
            <span className="text-sm text-gray-600">
              Page {page} of {pagination.totalPages}
            </span>
            <ButtonShadcn
              variant="outline"
              size="sm"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= pagination.totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </ButtonShadcn>
          </div>
        </div>
      )}

      {/* Manage Access */}
      {(someSelected || allSelected) ? (
        <button 
        className='cursor-pointer border-0 bg-transparent p-0 w-fit text-sm px-2'
        onClick={() => setView('ACCESS_CONTROL')}
        >
          Manage Access: {selectedSamples.length} selected
        </button>
      ) : (
        <div className='px-2 flex gap-2 items-center text-sm'>
            <p>Bulk Action Bar: 0 items selected</p>
            <p className='text-gray-500'>(Changes to "Manage Access" on click)</p>
        </div>
      )}
    </div>
  );
};

export default CatalogueTable;
